'use client';

import { Fragment, type CSSProperties } from 'react';
import styles from './QualityProcess.module.scss';
import { ChevronRight, RefreshCcw } from 'lucide-react';
import { useAOS } from '@/src/lib/hooks/useAOS';

export type QualityProcessStep = { title: string; description: string };
export type QualityProcessTool = { name: string; description: string };

export type QualityProcessProps = {
  eyebrow?: string;
  title?: string; // may contain *highlighted* gold <em> segments
  intro?: string; // positioning paragraph
  steps: QualityProcessStep[]; // REQUIRED, the 7-step sequence; numbers auto-generated (1..n)
  stepsLabel?: string; // e.g. "Le cycle de contrôle"
  centerTitle?: string; // e.g. "Chaque ticket, chaque tâche" (wheel center, desktop)
  cycleText?: string; // closing loop-back sentence showing quality is a cycle, not a chain
  toolsTitle?: string; // e.g. "Une toolchain adaptée au projet"
  tools?: QualityProcessTool[]; // SECONDARY implementation details — rendered small, clearly subordinate
  toolsDisclaimer?: string; // e.g. "La toolchain exacte est adaptée aux risques et contraintes de chaque projet."
};

/* Renders `*highlighted*` segments as gold <em>, following sibling convention */
const renderEmphasis = (text: string) => {
  const parts = text.split('*');
  return parts.map((part, i) =>
    i % 2 === 1 ? <em key={i}>{part}</em> : <Fragment key={i}>{part}</Fragment>
  );
};

const DEFAULT_STEPS: QualityProcessStep[] = [
  {
    title: 'Exigences cadrées avant le premier commit',
    description:
      'Critères d’acceptation, périmètre et niveaux de risque sont posés avec vous : chaque livrable sait dès le départ à quoi il sera mesuré.',
  },
  {
    title: 'Revue de code systématique',
    description:
      'Aucune ligne ne rejoint la branche principale sans relecture par un pair. Les standards du projet sont partagés et appliqués par tous.',
  },
  {
    title: 'Tests automatisés à chaque changement',
    description:
      'Tests unitaires et end-to-end s’exécutent à chaque modification. Un régression détectée bloque la livraison avant qu’elle n’atteigne la recette.',
  },
  {
    title: 'Qualimétrie continue',
    description:
      'Maintenabilité, duplication et dette technique sont suivies en continu pour garder un code lisible, documenté et évolutif.',
  },
  {
    title: 'Sécurité des dépendances',
    description:
      'Les dépendances sont surveillées et les correctifs proposés automatiquement. Les risques identifiés sont évalués et traités selon leur criticité.',
  },
  {
    title: 'Performance mesurée avant livraison',
    description:
      'Chaque version est mesurée sur la performance, l’accessibilité, le SEO et les bonnes pratiques web avant d’être mise entre vos mains.',
  },
  {
    title: 'Recette & transfert maîtrisés',
    description:
      'Vous validez sur un environnement de recette fidèle à la production. Le transfert s’accompagne d’une documentation et d’une prise de main complète.',
  },
];

const DEFAULT_TOOLS: QualityProcessTool[] = [
  { name: 'Jest', description: 'Tests unitaires et d’intégration exécutés à chaque commit.' },
  { name: 'Cypress', description: 'Tests end-to-end sur les parcours critiques de l’application.' },
  {
    name: 'Snyk',
    description: 'Détection et suivi des vulnérabilités dans les dépendances du projet.',
  },
  { name: 'Dependabot', description: 'Propositions de mises à jour automatisées des dépendances.' },
  {
    name: 'CodeClimate',
    description: 'Qualimétrie continue : maintenabilité, duplication et dette technique.',
  },
  {
    name: 'Lighthouse',
    description: 'Mesure la performance, l’accessibilité, le SEO et les bonnes pratiques web.',
  },
];

export function QualityProcess({
  eyebrow,
  title,
  intro,
  steps = DEFAULT_STEPS,
  stepsLabel,
  centerTitle,
  cycleText,
  toolsTitle,
  tools,
  toolsDisclaimer,
  lang = 'fr',
}: QualityProcessProps & { lang?: string }) {
  useAOS();
  const isEn = lang === 'en';

  const resolvedEyebrow = eyebrow ?? (isEn ? 'OUR QUALITY COMMITMENT' : 'NOTRE CONTRAT DE QUALITÉ');
  const resolvedTitle = title ?? (isEn ? 'Our *quality* commitment' : 'Notre contrat de *qualité*');
  const resolvedIntro =
    intro ??
    (isEn
      ? 'Quality is not a tool: it is a controlled delivery process, applied from the first commit to the final handover.'
      : 'La qualité n’est pas un outil : c’est un processus de livraison contrôlée, appliqué du premier commit jusqu’à la mise en production.');
  const resolvedToolsTitle =
    toolsTitle ??
    (isEn ? 'A toolchain tailored to the project' : 'Une toolchain adaptée au projet');
  const resolvedTools = tools ?? DEFAULT_TOOLS;
  const resolvedDisclaimer =
    toolsDisclaimer ??
    (isEn
      ? 'The exact toolchain is adapted to the risks and constraints of each project.'
      : 'La toolchain exacte est adaptée aux risques et contraintes de chaque projet.');
  const resolvedStepsLabel = stepsLabel ?? (isEn ? 'The quality cycle' : 'Le cycle de contrôle');
  const resolvedCenterTitle =
    centerTitle ?? (isEn ? 'Every ticket, every task' : 'Chaque ticket, chaque tâche');
  const resolvedCycleText =
    cycleText ??
    (isEn
      ? 'The cycle restarts: each handover feeds the scoping of the next iteration — every lesson learned strengthens step 1.'
      : 'Le cycle reprend : chaque transfert nourrit le cadrage de l’évolution suivante — chaque retour d’expérience renforce l’étape 1.');

  return (
    <section className={styles.section} aria-labelledby="quality-process-title">
      <div className={styles.inner}>
        <header className={styles.header} data-aos="fade-up" data-aos-duration="700">
          {resolvedEyebrow && <p className={styles.eyebrow}>{resolvedEyebrow}</p>}
          <h2 id="quality-process-title" className={styles.title}>
            {renderEmphasis(resolvedTitle)}
          </h2>
          {resolvedIntro && <p className={styles.intro}>{resolvedIntro}</p>}
        </header>

        {/* Desktop: the same steps arranged on a circular wheel —
            quality is a cycle applied to every ticket, not a pipeline. */}
        <div
          className={styles.cycleWheelWrap}
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-delay="100"
        >
          <p className={styles.timelineLabel}>{resolvedStepsLabel}</p>
          <div className={styles.cycleWheel}>
            <div className={styles.wheelRing} aria-hidden="true" />
            <svg className={styles.wheelArc} viewBox="0 0 600 600" aria-hidden="true">
              <circle
                cx="300"
                cy="300"
                r="298"
                fill="none"
                strokeWidth="3"
                pathLength={100}
                strokeDasharray="20 80"
              />
            </svg>
            <span className={styles.wheelArcArrow} aria-hidden="true">
              <ChevronRight size={18} />
            </span>
            <div className={styles.wheelCenter}>
              <span className={styles.wheelCenterIcon} aria-hidden="true">
                <RefreshCcw size={22} />
              </span>
              <p className={styles.wheelCenterTitle}>{resolvedCenterTitle}</p>
              <p className={styles.wheelCenterText}>{resolvedCycleText}</p>
            </div>
            <ol className={styles.wheelSteps}>
              {steps.map((step, index) => (
                <li
                  key={step.title}
                  className={styles.wheelStep}
                  style={
                    {
                      '--angle': `${index * (360 / steps.length)}deg`,
                    } as CSSProperties
                  }
                >
                  <div className={styles.wheelStepCard}>
                    <span className={styles.wheelStepNumber} aria-hidden="true">
                      {index + 1}
                    </span>
                    <h3 className={styles.wheelStepTitle}>{step.title}</h3>
                    <p className={styles.wheelStepDescription}>{step.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div
          className={styles.timelineWrapper}
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-delay="100"
        >
          <p className={styles.timelineLabel}>{resolvedStepsLabel}</p>
          <ol className={styles.steps}>
            {steps.map((step, index) => (
              <li key={step.title} className={styles.step}>
                <span className={styles.stepNumber} aria-hidden="true">
                  {index + 1}
                </span>
                <div className={styles.stepBody}>
                  <h3 className={styles.stepTitle}>{step.title}</h3>
                  <p className={styles.stepDescription}>{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className={styles.cycleClose} data-aos="fade-up" data-aos-duration="700">
            <span className={styles.cycleCloseIcon} aria-hidden="true">
              <RefreshCcw size={18} />
            </span>
            <p className={styles.cycleCloseText}>{resolvedCycleText}</p>
          </div>
        </div>

        {resolvedTools.length > 0 && (
          <aside
            className={styles.tools}
            aria-label={resolvedToolsTitle}
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-delay="150"
          >
            <h3 className={styles.toolsTitle}>{resolvedToolsTitle}</h3>
            <ul className={styles.toolsGrid}>
              {resolvedTools.map((tool) => (
                <li key={tool.name} className={styles.tool}>
                  <span className={styles.toolName}>{tool.name}</span>
                  <span className={styles.toolDescription}>{tool.description}</span>
                </li>
              ))}
            </ul>
            {resolvedDisclaimer && <p className={styles.toolsDisclaimer}>{resolvedDisclaimer}</p>}
          </aside>
        )}
      </div>
    </section>
  );
}
